import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import MainLayout from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Gift,
  Plus,
  Search,
  Loader2,
  Clock,
  Users,
  Euro,
  Hash,
  Check,
  X,
  Trash2,
  Copy,
  MoreVertical,
  User,
  Calendar,
  AlertCircle,
  Sparkles,
  TrendingUp
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const GiftCodesPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // Form state
  const [newCode, setNewCode] = useState({
    code: '',
    value_type: 'hours',
    value: '',
    max_uses: '1',
    assigned_client_id: '',
    description: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [codesRes, statsRes, clientsRes] = await Promise.all([
        api.get('/gift-codes/'),
        api.get('/gift-codes/stats'),
        api.get('/users/?role=client')
      ]);
      setCodes(codesRes.data);
      setStats(statsRes.data);
      setClients(clientsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const generateRandomCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = 'MSC-';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewCode({ ...newCode, code });
  };

  const handleCreateCode = async () => {
    if (!newCode.code.trim()) {
      toast.error('Veuillez entrer un code');
      return;
    }
    if (!newCode.value || parseFloat(newCode.value) <= 0) {
      toast.error('Veuillez entrer une valeur valide');
      return;
    }
    
    setCreating(true);
    try {
      await api.post('/gift-codes/', {
        code: newCode.code.trim(),
        value_type: newCode.value_type,
        value: parseFloat(newCode.value),
        max_uses: newCode.max_uses ? parseInt(newCode.max_uses) : null,
        assigned_client_id: newCode.assigned_client_id || null,
        description: newCode.description || null
      });
      
      toast.success('Code cadeau créé !');
      setShowCreateDialog(false);
      setNewCode({
        code: '',
        value_type: 'hours',
        value: '',
        max_uses: '1',
        assigned_client_id: '',
        description: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handleDeactivate = async (codeId) => {
    try {
      await api.put(`/gift-codes/${codeId}/deactivate`);
      toast.success('Code désactivé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la désactivation');
    }
  };

  const handleDelete = async (codeId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce code ?')) return;
    
    try {
      await api.delete(`/gift-codes/${codeId}`);
      toast.success('Code supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copié !');
  };

  const filteredCodes = codes.filter(code => {
    const matchesSearch = code.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         code.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         code.assigned_client_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filterStatus === 'active') return matchesSearch && code.is_active;
    if (filterStatus === 'used') return matchesSearch && code.current_uses > 0;
    if (filterStatus === 'inactive') return matchesSearch && !code.is_active;
    return matchesSearch;
  });

  const getStatusBadge = (code) => {
    if (!code.is_active) {
      return <Badge variant="outline" className="bg-gray-100 text-gray-600">Désactivé</Badge>;
    }
    if (code.max_uses && code.current_uses >= code.max_uses) {
      return <Badge variant="outline" className="bg-orange-100 text-orange-600">Épuisé</Badge>;
    }
    if (code.expires_at && new Date(code.expires_at) < new Date()) {
      return <Badge variant="outline" className="bg-red-100 text-red-600">Expiré</Badge>;
    }
    return <Badge variant="outline" className="bg-green-100 text-green-600">Actif</Badge>;
  };

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#1a365d] flex items-center gap-3" data-testid="gift-codes-title">
              <Gift className="w-8 h-8 text-purple-600" />
              Codes Cadeaux
            </h1>
            <p className="text-[#64748b] mt-1">
              Créez et gérez les codes cadeaux pour vos clients
            </p>
          </div>
          
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700" data-testid="create-code-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau code
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  Créer un code cadeau
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Code */}
                <div className="space-y-2">
                  <Label>Code</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newCode.code}
                      onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                      placeholder="Ex: MSC-CADEAU"
                      className="font-mono uppercase"
                      data-testid="new-code-input"
                    />
                    <Button type="button" variant="outline" onClick={generateRandomCode}>
                      <Hash className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Type de valeur */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type de valeur</Label>
                    <Select 
                      value={newCode.value_type} 
                      onValueChange={(value) => setNewCode({ ...newCode, value_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hours">Heures</SelectItem>
                        <SelectItem value="euros">Euros</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Valeur ({newCode.value_type === 'hours' ? 'heures' : '€'})</Label>
                    <Input
                      type="number"
                      value={newCode.value}
                      onChange={(e) => setNewCode({ ...newCode, value: e.target.value })}
                      placeholder={newCode.value_type === 'hours' ? 'Ex: 5' : 'Ex: 100'}
                      min="0"
                      step="0.5"
                      data-testid="new-code-value"
                    />
                  </div>
                </div>
                
                {/* Nombre d'utilisations */}
                <div className="space-y-2">
                  <Label>Nombre d'utilisations maximum</Label>
                  <Input
                    type="number"
                    value={newCode.max_uses}
                    onChange={(e) => setNewCode({ ...newCode, max_uses: e.target.value })}
                    placeholder="Laisser vide pour illimité"
                    min="1"
                  />
                  <p className="text-xs text-slate-500">Laisser vide pour un nombre illimité d'utilisations</p>
                </div>
                
                {/* Attribution à un client */}
                <div className="space-y-2">
                  <Label>Attribuer à un client (optionnel)</Label>
                  <Select 
                    value={newCode.assigned_client_id} 
                    onValueChange={(value) => setNewCode({ ...newCode, assigned_client_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Tous les clients" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Tous les clients</SelectItem>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.company_name || `${client.first_name} ${client.last_name}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Si un client est sélectionné, seul lui pourra utiliser ce code</p>
                </div>
                
                {/* Description */}
                <div className="space-y-2">
                  <Label>Description (optionnel)</Label>
                  <Textarea
                    value={newCode.description}
                    onChange={(e) => setNewCode({ ...newCode, description: e.target.value })}
                    placeholder="Note interne sur ce code..."
                    rows={2}
                  />
                </div>
                
                {/* Preview */}
                {newCode.code && newCode.value && (
                  <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                    <p className="text-sm text-purple-700 mb-2">Aperçu du code :</p>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-2xl font-bold text-purple-800">{newCode.code}</span>
                      <Badge className="bg-purple-600">
                        {newCode.value}{newCode.value_type === 'hours' ? 'h' : '€'}
                      </Badge>
                    </div>
                  </div>
                )}
                
                <Button 
                  onClick={handleCreateCode}
                  disabled={creating}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                  data-testid="confirm-create-code"
                >
                  {creating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Création...
                    </>
                  ) : (
                    <>
                      <Gift className="w-4 h-4 mr-2" />
                      Créer le code
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Gift className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total codes</p>
                    <p className="text-2xl font-bold">{stats.total_codes}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Check className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Codes actifs</p>
                    <p className="text-2xl font-bold">{stats.active_codes}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Clock className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Valeur en heures</p>
                    <p className="text-2xl font-bold">{stats.total_value_hours}h</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <TrendingUp className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Utilisations</p>
                    <p className="text-2xl font-bold">{stats.total_redemptions}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="card mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher par code, client ou description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder="Filtrer par statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les codes</SelectItem>
                  <SelectItem value="active">Actifs</SelectItem>
                  <SelectItem value="used">Utilisés</SelectItem>
                  <SelectItem value="inactive">Désactivés</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Codes List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : filteredCodes.length === 0 ? (
          <Card className="card">
            <CardContent className="py-12 text-center">
              <Gift className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Aucun code cadeau</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm ? 'Aucun code ne correspond à votre recherche' : 'Créez votre premier code cadeau pour commencer'}
              </p>
              {!searchTerm && (
                <Button onClick={() => setShowCreateDialog(true)} className="bg-gradient-to-r from-purple-600 to-pink-600">
                  <Plus className="w-4 h-4 mr-2" />
                  Créer un code
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredCodes.map((code) => (
              <Card key={code.id} className={cn(
                "card hover:shadow-md transition-shadow",
                !code.is_active && "opacity-60"
              )}>
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    {/* Code Info */}
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                        <Gift className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-xl font-bold text-purple-700">{code.code}</span>
                          <button 
                            onClick={() => copyToClipboard(code.code)}
                            className="p-1 hover:bg-slate-100 rounded transition-colors"
                          >
                            <Copy className="w-4 h-4 text-slate-400" />
                          </button>
                          {getStatusBadge(code)}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            {code.value_type === 'hours' ? (
                              <><Clock className="w-4 h-4" /> {code.value}h</>
                            ) : (
                              <><Euro className="w-4 h-4" /> {code.value}€</>
                            )}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {code.current_uses}/{code.max_uses || '∞'} utilisations
                          </span>
                          {code.assigned_client_name && (
                            <span className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              {code.assigned_client_name}
                            </span>
                          )}
                        </div>
                        {code.description && (
                          <p className="text-sm text-slate-400 mt-1">{code.description}</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="icon">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => copyToClipboard(code.code)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Copier le code
                          </DropdownMenuItem>
                          {code.is_active && (
                            <DropdownMenuItem onClick={() => handleDeactivate(code.id)}>
                              <X className="w-4 h-4 mr-2" />
                              Désactiver
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem 
                            onClick={() => handleDelete(code.id)}
                            className="text-red-600 focus:text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  
                  {/* Redemptions */}
                  {code.redemptions?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <p className="text-sm font-medium text-slate-600 mb-2">Utilisations :</p>
                      <div className="space-y-2">
                        {code.redemptions.slice(0, 3).map((redemption, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm p-2 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-slate-400" />
                              <span>{redemption.company_name || redemption.user_name}</span>
                            </div>
                            <div className="flex items-center gap-4 text-slate-500">
                              <span className="text-green-600 font-medium">+{redemption.hours_credited.toFixed(1)}h</span>
                              <span className="text-xs">
                                {new Date(redemption.redeemed_at).toLocaleDateString('fr-FR')}
                              </span>
                            </div>
                          </div>
                        ))}
                        {code.redemptions.length > 3 && (
                          <p className="text-xs text-slate-400 text-center">
                            +{code.redemptions.length - 3} autres utilisations
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default GiftCodesPage;
